import type { DocPageContent } from '../docs-content'

const page = {
  order: 9,
  slug: 'source-monitor',
  title: 'Flujo de trabajo del monitor de origen',
  description:
    'Previsualiza una fuente, marca un rango de entrada/salida, elige los destinos de conexión (patch) e inserta o sobrescribe en la línea de tiempo.',
  category: 'Edición principal',
  related: ['media', 'timeline', 'scene-browser'],
  sections: [
    {
      title: 'Abrir una fuente',
      blocks: [
        {
          kind: 'list',
          items: [
            'Haz doble clic en una tarjeta de medio, o usa **Abrir en el monitor de origen** desde Información de medios, para cargar una fuente.',
            'El encabezado del monitor muestra el nombre del archivo de origen, con un control de cierre para salir de él.',
            'La reproducción de la fuente es independiente de la vista previa de la línea de tiempo, por lo que puedes recorrer una fuente sin mover el cursor de reproducción de la línea de tiempo.',
            'Haz clic en el código de tiempo para alternar entre la visualización de código de tiempo y número de fotograma.',
          ],
        },
      ],
    },
    {
      title: 'Marcar un rango de entrada/salida',
      blocks: [
        {
          kind: 'steps',
          items: [
            'Ve al primer fotograma que quieras y presiona `I` (**Marcar entrada**).',
            'Ve al fotograma posterior al último que quieras y presiona `O` (**Marcar salida**); el punto de salida es exclusivo.',
            'Ajusta con precisión arrastrando el controlador de entrada, el de salida, o todo el rango en la franja.',
            'Usa **Reproducir de entrada a salida** para revisar solo el rango, o `Alt+X` para borrarlo y usar la fuente completa.',
          ],
        },
      ],
    },
    {
      title: 'Elegir los destinos de conexión (patch)',
      blocks: [
        {
          kind: 'list',
          items: [
            'El interruptor **V** activa o desactiva el destino de conexión de video de la fuente; el interruptor **A** hace lo mismo para el audio.',
            'Cada interruptor tiene un selector de destino: **Auto**, una pista específica por nombre, o **Crear al editar** para hacer una pista nueva.',
            'El video con sonido coloca un par vinculado de video y audio en las pistas de destino V y A.',
            'La pista activa de la línea de tiempo rellena de antemano los destinos V y A correspondientes.',
          ],
        },
      ],
    },
    {
      title: 'Insertar y sobrescribir',
      blocks: [
        {
          kind: 'paragraph',
          text: 'Ambas ediciones colocan el rango marcado en el fotograma actual del cursor de reproducción de la línea de tiempo, y luego avanzan el cursor hasta el final del nuevo clip.',
        },
        {
          kind: 'table',
          headers: ['Edición', 'Tecla', 'Qué hace'],
          rows: [
            [
              'Insertar',
              '`,`',
              'Coloca el rango y empuja hacia la derecha todo el material posterior en las pistas de destino.',
            ],
            [
              'Sobrescribir',
              '`.`',
              'Reemplaza el material de la línea de tiempo que se encuentra bajo el rango, dejando en su lugar los clips circundantes.',
            ],
          ],
        },
        {
          kind: 'note',
          tone: 'info',
          text: 'Las imágenes usan por defecto un clip de **tres segundos** al editar, a menos que marques un rango más corto.',
        },
      ],
    },
    {
      title: 'Si una edición no se aplica',
      blocks: [
        {
          kind: 'note',
          tone: 'warning',
          text: 'Primero activa al menos uno de los destinos de conexión **V** o **A**. Para editar audio, activa **A**; para editar un video sin sonido o una imagen, activa **V**. Una pista de destino bloqueada también impide la edición: desbloquéala e inténtalo de nuevo.',
        },
      ],
    },
  ],
} satisfies DocPageContent

export default page
